import { authenticatedFastApiAxiosInstanceAllowEmptyString as http } from "@/lib/data/http";
import { z } from "zod";
import {
  automationSchema,
  automationSummarySchema,
  automationFileSchema,
  automationRunSchema,
  capabilityOptionSchema,
  contentTagSchema,
  type AutomationConfig,
  type AutomationScope,
  type ContentTagInput,
} from "@/lib/data/schemas/automations";

const pageSchema = <T extends z.ZodTypeAny>(item: T) =>
  z.object({ items: z.array(item), nextCursor: z.string().nullish() });
export async function listAutomations(scope: AutomationScope, cursor?: string) {
  return pageSchema(automationSummarySchema).parse(
    (await http.get("automations", { params: { limit: 50, cursor, scope } }))
      .data,
  );
}
export async function listContentTags(cursor?: string) {
  return pageSchema(contentTagSchema).parse(
    (await http.get("content-tags", { params: { limit: 200, cursor } })).data,
  );
}
export async function listAutomationCapabilities() {
  return z
    .object({ items: z.array(capabilityOptionSchema) })
    .parse((await http.get("automations/capabilities")).data).items;
}
export async function saveAutomation(
  config: AutomationConfig,
  id?: string,
  expectedRevisionId?: string,
) {
  const { scope, ...changes } = config;
  const result = id
    ? await http.patch(`automations/${id}`, { ...changes, expectedRevisionId })
    : await http.post("automations", { ...changes, scope });
  return automationSchema.parse(result.data);
}
export async function setAutomationEnabled(
  id: string,
  enabled: boolean,
  expectedRevisionId: string,
) {
  return automationSchema.parse(
    (
      await http.patch(`automations/${id}`, {
        enabled,
        ...(enabled ? { expectedRevisionId } : {}),
      })
    ).data,
  );
}
export async function deleteAutomation(id: string) {
  await http.delete(`automations/${id}`);
}
export async function saveContentTag(input: ContentTagInput, id?: string) {
  const result = id
    ? await http.patch(`content-tags/${id}`, input)
    : await http.post("content-tags", input);
  return contentTagSchema.parse(result.data);
}
export async function deleteContentTag(id: string) {
  await http.delete(`content-tags/${id}`);
}
export async function listAutomationRuns(id: string, cursor?: string) {
  return pageSchema(automationRunSchema).parse(
    (
      await http.get(`automations/${id}/runs`, {
        params: { limit: 30, cursor },
      })
    ).data,
  );
}

export async function listAutomationFiles(
  query: string,
  cursor?: string,
  includeUnprocessed = false,
) {
  return z
    .object({
      data: z.array(automationFileSchema),
      nextCursor: z.string().nullable(),
    })
    .parse(
      (
        await http.get("file-contents", {
          params: { query, cursor, limit: 30, includeUnprocessed },
        })
      ).data,
    );
}
export async function startManualAutomation(
  id: string,
  fileContentId: string,
  idempotencyKey: string,
) {
  return automationRunSchema.parse(
    (
      await http.post(
        `automations/${id}/runs`,
        { fileContentId },
        { headers: { "Idempotency-Key": idempotencyKey } },
      )
    ).data,
  );
}

export async function getAutomation(id: string) {
  return automationSchema.parse((await http.get(`automations/${id}`)).data);
}

export async function listAutomationUploaders(query: string, cursor?: string) {
  return z
    .object({
      items: z.array(z.object({ id: z.string(), name: z.string() })),
      nextCursor: z.string().nullable(),
    })
    .parse(
      (
        await http.get("automations/uploaders", {
          params: { search: query, cursor, limit: 50 },
        })
      ).data,
    );
}
